/**
 * UAPC Anytime - Admin Reports Page
 * @module AdminReports
 * @author Roohi Fatima - Frontend Developer
 * @description System-wide sales and analytics reports
 * @version 1.0.0
 * Git: Roohi Fatima <roohi@linesoft.com> - Admin reporting
 */

import React, { useState, useEffect } from 'react';
import { DollarSign, Package, ShoppingCart, Search, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReports = () => {
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [salesRes, inventoryRes, searchRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/reports/sales`),
        axios.get(`${API_URL}/api/admin/reports/inventory`),
        axios.get(`${API_URL}/api/admin/reports/searches`)
      ]);
      setSalesData(salesRes.data);
      setInventoryData(inventoryRes.data);
      setSearchData(searchRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-48" />
            <div className="grid grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800" />
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8" data-testid="admin-reports">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          System Reports
        </h1>

        <Tabs defaultValue="sales">
          <TabsList className="mb-8">
            <TabsTrigger value="sales" className="font-heading uppercase" data-testid="tab-sales">Sales</TabsTrigger>
            <TabsTrigger value="inventory" className="font-heading uppercase" data-testid="tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="searches" className="font-heading uppercase" data-testid="tab-searches">Search Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="sales">
            {/* Sales Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Total Sales</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="sales-total">
                  ${salesData?.total_sales?.toFixed(2) || '0.00'}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Total Orders</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="sales-orders">
                  {salesData?.total_orders || 0}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Avg Order</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white">
                  ${salesData?.total_orders > 0 ? (salesData.total_sales / salesData.total_orders).toFixed(2) : '0.00'}
                </div>
              </div>
            </div>

            {/* Vendor Breakdown */}
            {salesData?.vendor_breakdown && Object.keys(salesData.vendor_breakdown).length > 0 && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6">
                  Sales by Vendor
                </h3>
                <div className="space-y-3">
                  {Object.entries(salesData.vendor_breakdown).map(([vendorId, data]) => (
                    <div key={vendorId} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <div className="font-mono text-sm text-slate-500">{vendorId.slice(0, 8)}</div>
                      <div className="flex items-center gap-6">
                        <span className="text-slate-600 dark:text-slate-400">{data.count} sales</span>
                        <span className="font-heading font-bold text-blue-600">${data.total.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="inventory">
            {/* Inventory Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Total Parts</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-total">
                  {inventoryData?.total_parts || 0}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Low Stock</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-low">
                  {inventoryData?.low_stock_count || 0}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <Package className="w-6 h-6 text-red-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Out of Stock</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-out">
                  {inventoryData?.out_of_stock_count || 0}
                </div>
              </div>
            </div>

            {/* By Category */}
            {inventoryData?.by_category?.length > 0 && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6">
                  Inventory by Category
                </h3>
                <div className="space-y-3">
                  {inventoryData.by_category.map((cat, index) => (
                    <div key={cat._id || index} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <span className="font-medium text-slate-900 dark:text-white">{cat._id || 'Uncategorized'}</span>
                      <div className="flex items-center gap-6">
                        <span className="text-slate-600 dark:text-slate-400">{cat.count} parts</span>
                        <span className="font-heading font-bold text-blue-600">${cat.total_value?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="searches">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Popular Searches */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6">
                  Popular Searches
                </h3>
                {searchData?.popular_searches?.length > 0 ? (
                  <div className="space-y-3">
                    {searchData.popular_searches.map((search, index) => (
                      <div key={index} className="flex items-center justify-between py-2">
                        <span className="text-slate-700 dark:text-slate-300">{search._id}</span>
                        <span className="font-mono text-sm text-slate-500">{search.count} searches</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No search data yet</p>
                )}
              </div>

              {/* Failed Searches */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6 flex items-center gap-2">
                  <Search className="w-5 h-5 text-orange-500" />
                  Failed Searches
                </h3>
                {searchData?.failed_searches?.length > 0 ? (
                  <div className="space-y-3">
                    {searchData.failed_searches.slice(0, 10).map((search, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <span className="text-slate-700 dark:text-slate-300">{search.query}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(search.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No failed searches</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
